import {
  ARRANGEMENT_LIST_DIVISIONS_MAX,
  ARRANGEMENT_TYPE_ANY,
  OBJECT_ID_REGEX,
  isArrangementBooklet,
  isArrangementTypeNarrowing,
  type ArrangementBooklet,
  type ArrangementType,
  type ArrangementTypeFilter,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] WHAT "ADJUST FILTERS" HAS APPLIED, AS A VALUE — AND NOTHING ELSE.
 *
 *  Manage Arrangements and Durga Puja PA render one `ArrangementsList`, and both read ONE
 *  filter: the divisions an operator narrowed the list to, kept until they sign out. The store
 *  that holds it is `hooks/useArrangementListFilters.ts`; this file is the part with no I/O —
 *  the shape, the one canonical form, and the stored payload's reader and writer.
 *
 *  ⚠️ PURE, AND IT NEVER NAMES A STORAGE GLOBAL. `lib/eslintConfig.test.ts` E4 holds every
 *  non-test file under `src/lib` to that by a source census, because the `src/lib` lint block's
 *  storage ban is about CREDENTIALS and cannot see a member expression reached any other way.
 *  The writer is a hook, which is where that block's own message sends a preference.
 *
 *  ⚠️ ONE CANONICAL FORM: lower-cased, de-duplicated, sorted, valid ObjectIds only. The store
 *  writes it, the request sends it and the query key carries it, so two selections that mean the
 *  same divisions are one request and one cache entry, whatever order they were picked in.
 *
 *  ⚠️ AND ONE EMPTY REFERENCE. `EMPTY_ARRANGEMENT_LIST_FILTERS` is frozen and every "nothing
 *  applied" answer below returns THAT object, so `useSyncExternalStore` sees an unchanged
 *  snapshot rather than a fresh `{ divisionOrgUnitIds: [] }` on every read — which React treats
 *  as a change and re-renders for ever.
 *
 *  ⚠️ A NEW FILTER IS A FIELD HERE. The type is an object rather than a bare id list so that a
 *  second filter is a second key, and `activeFilterCount` is the one sum the button's "Filters
 *  applied · N" reads. ⚠️ [TYPE-FILTER] `type` IS THE SECOND — "Order type", for Manage Arrangements
 *  only: the Durga Puja PA list is its own type, so there it is held, never applied or counted.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type ArrangementListFilters = {
  readonly divisionOrgUnitIds: readonly string[];
  /** `any` (every type — the default), `none` (regular orders only) or one registered type. */
  readonly type: ArrangementTypeFilter;
  /**
   * [DP2026] Which booklet's orders a TYPED list shows — `all` (the default), `current` or `earlier`
   * (`ARRANGEMENT_BOOKLETS`' header). The Durga Puja PA list's own filter, held back on Manage
   * Arrangements exactly as `type` is held back on a typed list.
   */
  readonly booklet: ArrangementBooklet;
};

const NO_IDS: readonly string[] = Object.freeze([]);

/** THE empty value. Compare with `toBe`: every empty answer in this file is this object. */
export const EMPTY_ARRANGEMENT_LIST_FILTERS: ArrangementListFilters = Object.freeze({
  divisionOrgUnitIds: NO_IDS,
  type: ARRANGEMENT_TYPE_ANY,
  booklet: 'all',
});

/**
 * ⚠️ PER OFFICER, AND A PREFIX RATHER THAN ONE KEY. A tab still showing officer X must never
 * overwrite officer Y's filter after Y signs in elsewhere — `pams.app.locationShare.<officerId>`
 * is the precedent — and sign-out sweeps the PREFIX, so no officer's value outlives the session.
 * Namespaced like every other preference here (`pams.theme`, `pams.columns.*`).
 */
export const LIST_FILTERS_STORAGE_PREFIX = 'pams.arrangement-filters.';

export const listFiltersStorageKey = (officerId: string): string => `${LIST_FILTERS_STORAGE_PREFIX}${officerId}`;

/**
 * The stored payload's version. A change an old reader would MISREAD bumps it, and an old value then reads
 * as empty. ⚠️ [TYPE-FILTER] `type` did not: it is written only when it narrows, so a payload without it is
 * All, and an older tab's reader takes `{ v, divisionOrgUnitIds }` alone and passes over it. A bump would
 * have made the two bundles erase each other's divisions for as long as both were open. [DP2026] `booklet`
 * likewise: written only when it narrows, and an older reader passes over it.
 */
const STORED_VERSION = 1;

const isObjectId = (value: unknown): value is string => typeof value === 'string' && OBJECT_ID_REGEX.test(value);

const isTypeFilter = (value: unknown): value is ArrangementTypeFilter =>
  value === ARRANGEMENT_TYPE_ANY || isArrangementTypeNarrowing(value);

/**
 * The one form a filter is held, stored, sent and keyed in.
 *
 * ⚠️ A NON-ID IS DROPPED, NEVER SENT. The server answers a malformed id with a 422, and a 422 on
 * a value that persists until sign-out would break both lists in every tab until somebody found
 * the Clear button.
 *
 * ⚠️ THE CAP IS THE SERVER'S OWN (`ARRANGEMENT_LIST_DIVISIONS_MAX`), so nothing this produces can
 * be refused for its length. The modal caps the picker at the same number, so the slice binds
 * only on input nobody could have made through the screen.
 */
export const canonicalListFilters = (next: ArrangementListFilters): ArrangementListFilters => {
  const ids = [...new Set(next.divisionOrgUnitIds.filter(isObjectId).map((id) => id.toLowerCase()))]
    .sort()
    .slice(0, ARRANGEMENT_LIST_DIVISIONS_MAX);
  /* A type no list could send is every type — the direction that hides nothing. */
  const type = isTypeFilter(next.type) ? next.type : ARRANGEMENT_TYPE_ANY;
  /* A booklet no list could send is every booklet — the same direction. */
  const booklet = isArrangementBooklet(next.booklet) ? next.booklet : 'all';
  if (ids.length === 0 && type === ARRANGEMENT_TYPE_ANY && booklet === 'all') return EMPTY_ARRANGEMENT_LIST_FILTERS;
  return Object.freeze({ divisionOrgUnitIds: ids.length === 0 ? NO_IDS : Object.freeze(ids), type, booklet });
};

/** Whether two filters would send the same request — compared in canonical form, so pick order is irrelevant. */
export const sameListFilters = (a: ArrangementListFilters, b: ArrangementListFilters): boolean => {
  const left = canonicalListFilters(a);
  const right = canonicalListFilters(b);
  return (
    left.type === right.type &&
    left.booklet === right.booklet &&
    left.divisionOrgUnitIds.length === right.divisionOrgUnitIds.length &&
    left.divisionOrgUnitIds.every((id, index) => id === right.divisionOrgUnitIds[index])
  );
};

/**
 * The stored payload, or `null` when nothing is applied — and `null` REMOVES the key, so
 * clearing a filter leaves nothing behind rather than an entry that says "nothing".
 */
export const serialiseListFilters = (filters: ArrangementListFilters): string | null => {
  const canonical = canonicalListFilters(filters);
  if (canonical === EMPTY_ARRANGEMENT_LIST_FILTERS) return null;
  return JSON.stringify({
    v: STORED_VERSION,
    divisionOrgUnitIds: canonical.divisionOrgUnitIds,
    ...(canonical.type === ARRANGEMENT_TYPE_ANY ? {} : { type: canonical.type }),
    ...(canonical.booklet === 'all' ? {} : { booklet: canonical.booklet }),
  });
};

/**
 * Reads a stored payload back.
 *
 * ⚠️ ANYTHING WRONG READS AS NOTHING APPLIED — THE EMPTY REFERENCE, NEVER A PARTIAL VALUE. Bad
 * JSON, another version, a missing or non-array list, one member that is not an ObjectId, more
 * members than the server accepts, or a `type` no list can send (a retired one included — an absent
 * `type` is All). A hand-edited or half-written value is not trusted in part:
 * dropping the one bad id would silently apply a narrower filter than anybody chose, and
 * truncating an over-long list would apply an arbitrary subset of it. Unfiltered is the
 * direction that hides nothing, and the next Apply rewrites the key.
 */
export const parseStoredListFilters = (raw: string | null): ArrangementListFilters => {
  if (raw === null) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return EMPTY_ARRANGEMENT_LIST_FILTERS;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  const { v, divisionOrgUnitIds, type, booklet } = value as Record<string, unknown>;
  if (v !== STORED_VERSION || !Array.isArray(divisionOrgUnitIds)) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  if (divisionOrgUnitIds.length > ARRANGEMENT_LIST_DIVISIONS_MAX) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  if (!divisionOrgUnitIds.every(isObjectId)) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  if (type !== undefined && !isTypeFilter(type)) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  if (booklet !== undefined && !isArrangementBooklet(booklet)) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  return canonicalListFilters({ divisionOrgUnitIds, type: type ?? ARRANGEMENT_TYPE_ANY, booklet: booklet ?? 'all' });
};

/**
 * The number the button reads — every value selected, across every filter THIS list honours. On a typed
 * list (`fixedType`) the type is the list's own, so a stored "Order type" is not counted there. [DP2026] The
 * booklet is the reverse: counted on a typed list only.
 */
export const activeFilterCount = (filters: ArrangementListFilters, fixedType?: ArrangementType): number =>
  filters.divisionOrgUnitIds.length +
  (fixedType === undefined && filters.type !== ARRANGEMENT_TYPE_ANY ? 1 : 0) +
  (fixedType !== undefined && filters.booklet !== 'all' ? 1 : 0);
